/**
 * MultisigScreen — eosio.msig proposals for the active account: approve,
 * unapprove, execute, cancel. Flags proposals that touch
 * updateauth/linkauth/deleteauth/unlinkauth before you approve them blind —
 * the same signal blocksfinity.com's /msigs page shows.
 *
 * Proposing new multisig transactions from scratch is a power-user flow
 * we don't build a form for yet — this screen manages proposals that
 * already exist (made here or elsewhere) and name this account as
 * proposer or as a requested approver.
 */

import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import type { Session } from '@wharfkit/session'
import {
  MSIG_APP_URL,
  listMsigProposalsByProposer,
  approveMsig,
  unapproveMsig,
  execMsig,
  cancelMsig,
  type MsigProposalSummary,
} from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import DAppWebView from '../components/DAppWebView'

export default function MultisigScreen() {
  const { t } = useTranslation()
  const { activeAccount, activeSession, goBack } = useWalletStore()

  const [proposals, setProposals] = useState<MsigProposalSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [openUrl, setOpenUrl] = useState<string | null>(null)

  // MSIG.app is a real dApp (it asks for a signature), so on native we open it
  // inside DAppWebView, where our window.properwallet bridge is injected. On web
  // that fallback is an <iframe> and third-party sites refuse framing, so there
  // we hand it to a normal tab.
  const openMsigApp = () => {
    if (Capacitor.isNativePlatform()) setOpenUrl(MSIG_APP_URL)
    else window.open(MSIG_APP_URL, '_blank', 'noopener,noreferrer')
  }

  const load = () => {
    if (!activeAccount) return
    setLoading(true)
    setError('')
    listMsigProposalsByProposer(activeAccount.name)
      .then(setProposals)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [activeAccount?.name]) // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (
    proposalName: string,
    fn: (session: Session, signingPermission: string) => Promise<string>,
  ) => {
    if (!activeAccount || !activeSession) return
    setBusyKey(proposalName)
    setError('')
    try {
      const session = activeSession as Session
      const signingPermission = session.permission.toString()
      await fn(session, signingPermission)
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyKey(null)
    }
  }

  if (openUrl) {
    return <DAppWebView url={openUrl} onClose={() => setOpenUrl(null)} />
  }

  return (
    <div className="flex flex-col h-full bg-surface">
      <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={goBack} className="text-gray-400 text-sm">← {t('common.back')}</button>
        <h2 className="text-white font-semibold flex-1 text-center">{t('multisig.title')}</h2>
        <button onClick={load} className="text-gray-400 text-sm">{t('multisig.refresh')}</button>
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-3">
        <p className="text-xs text-gray-400">{t('multisig.subtitle')}</p>

        <button onClick={openMsigApp} className="btn-ghost w-full" style={{ height: 40, fontSize: 13 }}>
          {t('multisig.openMsigApp')} ↗
        </button>

        {error ? <p className="text-danger text-xs text-center">{error}</p> : null}

        {loading ? (
          <p className="text-xs text-gray-500 text-center py-6">{t('common.loading')}</p>
        ) : proposals.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-6">{t('multisig.noProposals')}</p>
        ) : (
          proposals.map(p => {
            const key = p.proposalName
            const busy = busyKey === key
            const approvedCount = p.providedApprovals.length
            const requestedCount = p.requestedApprovals.length
            return (
              <div key={key} className="card space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-white font-mono text-sm">{p.proposalName}</p>
                  {p.hasAuthRisk && (
                    <span
                      className="text-xs font-semibold"
                      style={{
                        color: '#ef4444',
                        background: 'rgba(239,68,68,0.12)',
                        border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: 6,
                        padding: '2px 8px',
                      }}
                    >
                      ⚠ {t('multisig.riskBadge')}
                    </span>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  {t('multisig.proposer')}: <span className="font-mono">{p.proposer}</span>
                </p>
                <p className="text-xs text-gray-500">
                  {t('multisig.actions')}: {p.actions.map(a => `${a.account}::${a.name}`).join(', ') || '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {t('multisig.approvals')}: {approvedCount}/{requestedCount}
                </p>
                {p.expiration && (
                  <p className="text-xs text-gray-500">{t('multisig.expiration')}: {p.expiration}</p>
                )}

                <div className="flex gap-2 flex-wrap pt-1">
                  <button
                    className="btn-secondary text-xs py-2 px-3"
                    disabled={busy}
                    onClick={() => act(key, (s, perm) => approveMsig(s, p.proposer, p.proposalName, activeAccount!.name, perm))}
                  >
                    {t('multisig.approve')}
                  </button>
                  <button
                    className="btn-secondary text-xs py-2 px-3"
                    disabled={busy}
                    onClick={() => act(key, (s, perm) => unapproveMsig(s, p.proposer, p.proposalName, activeAccount!.name, perm))}
                  >
                    {t('multisig.unapprove')}
                  </button>
                  <button
                    className="btn-primary text-xs py-2 px-3"
                    disabled={busy}
                    onClick={() => act(key, (s, perm) => execMsig(s, p.proposer, p.proposalName, activeAccount!.name, perm))}
                  >
                    {t('multisig.execute')}
                  </button>
                  <button
                    className="btn-danger text-xs py-2 px-3"
                    disabled={busy}
                    onClick={() => {
                      if (confirm(t('multisig.confirmCancel'))) {
                        void act(key, (s, perm) => cancelMsig(s, p.proposer, p.proposalName, activeAccount!.name, perm))
                      }
                    }}
                  >
                    {t('multisig.cancel')}
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
